import React, { useEffect, useRef, useState } from 'react';
import { styled } from 'linaria/react';
import { useIntl } from '@jetshop/intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faCheck } from '@fortawesome/pro-light-svg-icons';
import { theme } from '../../Theming/Theming';
import {
  subscribeToKlaviyoBackInStock,
  subscribeToKlaviyoList
} from './klaviyoBackInStockApi';

const Row = styled('div')`
  width: 100%;
  margin-bottom: 20px;
`;

/* The buy row is hidden on a sold out article, so this is the page's only live
   action and takes the filled treatment the buy button would have had. */
const NotifyButton = styled('button')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 46px;
  padding: 0 1rem;
  background: ${theme.productPage.buyButton.background};
  border: 0;
  color: ${theme.productPage.buyButton.color};
  font-family: inherit;
  font-size: 1rem;
  line-height: 1.3;
  text-transform: none;
  letter-spacing: 0;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  ${theme.above.lg} {
    height: 60px;
    font-size: 1.1rem;
  }

  svg {
    flex: 0 0 auto;
    width: 1.05em;
    height: 1.05em;
    margin-right: 0.6em;
  }

  &:hover,
  &:focus-visible {
    opacity: 0.88;
    outline: none;
  }
`;

/* One bordered box holding the field and its submit, so the open state reads as
   the same object the button turned into rather than two stacked controls. */
const Field = styled('form')`
  display: flex;
  width: 100%;
  height: 46px;
  border: 1px solid ${theme.colors.font};
  background: ${theme.colors.white};

  ${theme.above.lg} {
    height: 60px;
  }

  input {
    flex: 1 1 auto;
    min-width: 0;
    height: 100%;
    padding: 0 0.875rem;
    border: 0;
    background: transparent;
    font-family: inherit;
    font-size: 1rem;
    color: ${theme.colors.font};
  }

  input::placeholder {
    color: ${theme.colors.greys[4]};
  }

  input:focus {
    outline: none;
  }

  button {
    flex: 0 0 auto;
    height: 100%;
    padding: 0 1.375rem;
    border: 0;
    background: ${theme.colors.font};
    color: ${theme.colors.white};
    font-family: inherit;
    font-size: 1rem;
    letter-spacing: 0.02em;
    cursor: pointer;
    transition: opacity 0.15s ease;
  }

  button:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const Note = styled('p')`
  margin: 0.5rem 0 0 0;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: ${theme.colors.greys[5]};
`;

/* Marketing consent, so it is never pre-ticked and the label is the customer's
   own words for what they are agreeing to. */
const OptIn = styled('label')`
  display: flex;
  align-items: flex-start;
  margin-top: 0.625rem;
  cursor: pointer;

  input {
    flex: 0 0 auto;
    width: 1rem;
    height: 1rem;
    margin: 0.125rem 0.5rem 0 0;
    cursor: pointer;
    accent-color: ${theme.colors.font};
  }

  span {
    font-size: 0.8125rem;
    line-height: 1.45;
    color: ${theme.colors.greys[5]};
  }
`;

const ErrorNote = styled('p')`
  margin: 0.5rem 0 0 0;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: ${theme.colors.error};
`;

/* Once the customer is on the list there is nothing left to do here, so the
   confirmation drops the border and lets the block go quiet. */
const Success = styled('div')`
  display: flex;
  align-items: flex-start;
  width: 100%;
  padding: 0.125rem 0;

  svg {
    flex: 0 0 auto;
    width: 1rem;
    height: 1rem;
    margin: 0.2rem 0.625rem 0 0;
    color: ${theme.colors.success};
  }

  p {
    margin: 0;
    font-size: 1rem;
    line-height: 1.45;
    color: ${theme.colors.font};
  }

  p + p {
    margin-top: 0.125rem;
    font-size: 0.8125rem;
    color: ${theme.colors.greys[5]};
  }
`;

/* Replaces Jetshop's stock notification form on shops that run Klaviyo. */
function KlaviyoBackInStock({ articleNumber, companyId, listId }) {
  const t = useIntl();
  // idle | active | submitting | submitted | failed
  const [status, setStatus] = useState('idle');
  const [email, setEmail] = useState('');
  const [wantsNewsletter, setWantsNewsletter] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setStatus('idle');
    setEmail('');
    setWantsNewsletter(false);
  }, [articleNumber]);

  useEffect(() => {
    if (status === 'active' && inputRef.current) inputRef.current.focus();
  }, [status]);

  const submitting = status === 'submitting';
  const submitted = status === 'submitted';
  const failed = status === 'failed';
  const open = status === 'active' || submitting || failed;
  const disabled = email.trim().length === 0 || submitting;

  const submit = async e => {
    e.preventDefault();
    if (submitting || submitted) return;
    setStatus('submitting');
    try {
      const address = email.trim();
      await subscribeToKlaviyoBackInStock({
        companyId,
        email: address,
        articleNumber
      });
      if (wantsNewsletter && listId) {
        try {
          await subscribeToKlaviyoList({ companyId, email: address, listId });
        } catch (consentError) {
          console.error(
            'Klaviyo newsletter consent failed',
            consentError,
            consentError.details
          );
        }
      }
      setStatus('submitted');
    } catch (err) {
      console.error('Klaviyo back in stock error', err, err.details);
      setStatus('failed');
    }
  };

  const note = t('We will e-mail you as soon as the item is back in stock.');

  return (
    <Row>
      {submitted ? (
        <Success role="status">
          <FontAwesomeIcon icon={faCheck} />
          <div>
            <p data-testid="notify-when-back-success">
              {t('You are now subscribed.')}
            </p>
            <p>{note}</p>
          </div>
        </Success>
      ) : open ? (
        <>
          <Field onSubmit={submit} aria-busy={submitting}>
            <input
              ref={inputRef}
              type="email"
              id="klaviyo-back-in-stock"
              data-testid="notify-when-back-input"
              aria-label={t('E-mail address')}
              placeholder={t('E-mail address')}
              required
              disabled={submitting}
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            <button
              type="submit"
              data-testid="notify-when-back-ok"
              disabled={disabled}
            >
              {t('OK')}
            </button>
          </Field>
          {listId && (
            <OptIn>
              <input
                type="checkbox"
                data-testid="notify-when-back-newsletter"
                checked={wantsNewsletter}
                disabled={submitting}
                onChange={e => setWantsNewsletter(e.target.checked)}
              />
              <span>
                {t('Yes, I also want newsletters and offers by e-mail.')}
              </span>
            </OptIn>
          )}
          {failed ? (
            <ErrorNote role="alert">
              {t(
                'Something went wrong. Please check your email and try again.'
              )}
            </ErrorNote>
          ) : (
            <Note>{note}</Note>
          )}
        </>
      ) : (
        <NotifyButton
          type="button"
          onClick={() => setStatus('active')}
          data-testid="notify-when-back-button"
        >
          <FontAwesomeIcon icon={faBell} />
          {t('Notify me when back in stock')}
        </NotifyButton>
      )}
    </Row>
  );
}

export default KlaviyoBackInStock;
